import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { StartIntent } from '../core/seeds/startIntent';
import { coerceStartIntent } from '../core/seeds/startIntent';

export interface OnboardingContextValue {
  isNewUser: boolean;
  isInvited: boolean;
  /** @deprecated Read `startIntent.seed` ({kind:'template'}) instead. Kept
   *  for the genogram AI-suggestions panel until it migrates. */
  sourceTemplateId: string | null;
  loadDemoData: boolean;
  /** @deprecated The org sample is a Seed Registry provider now
   *  (`seed=sample:org-25-sample`). Only legacy `onboardingMode` URLs set
   *  this. */
  loadOrgOptionsSampleData: boolean;
  orgOptions: boolean;
  useCase: string | null;
  startView: string | null;
  assistantPrompt: string | null;
  genogramSourcePromptKey: string | null;
  /** @deprecated Genogram wizard policy rides `startIntent.wizard`. */
  genogramStartMode: 'blank-direct' | 'scenario' | null;
  /** @deprecated Derived from `startIntent.panel` when not set explicitly. */
  networkStartPanel: 'shapes' | 'import' | 'resources' | null;
  /** Blueprint provider dialect selected before opening the editor. */
  networkDialectId: string | null;
  genoWizard: string | null;
  hierarchyChartWizard: string | null;
  /** Canonical typed creation intent. The deprecated per-domain fields
   *  above are derived from this during migration; new consumers MUST
   *  read `startIntent` directly. */
  startIntent: StartIntent | null;
}

const DEFAULT_VALUE: OnboardingContextValue = {
  isNewUser: false,
  isInvited: false,
  sourceTemplateId: null,
  loadDemoData: false,
  loadOrgOptionsSampleData: false,
  orgOptions: false,
  useCase: null,
  startView: null,
  assistantPrompt: null,
  genogramSourcePromptKey: null,
  genogramStartMode: null,
  networkStartPanel: null,
  networkDialectId: null,
  genoWizard: null,
  hierarchyChartWizard: null,
  startIntent: null,
};

/** Light structural coercion of a stored StartIntent (it's our own data,
 *  so a shallow shape check is enough). */
const OnboardingContext = createContext<OnboardingContextValue>(DEFAULT_VALUE);

function parseFlags(raw: unknown): { isNewUser: boolean; isInvited: boolean } {
  if (typeof raw !== 'string') {
    return { isNewUser: false, isInvited: false };
  }

  const tokens = raw
    .split(/[,\s]+/)
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

  return {
    isNewUser: tokens.includes('new'),
    isInvited: tokens.includes('invited'),
  };
}

function readAndClear(diagramId: string): OnboardingContextValue {
  if (typeof window === 'undefined') {
    return DEFAULT_VALUE;
  }

  const key = `onboarding:${diagramId}`;

  try {
    const stored = window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
    if (!stored) {
      return DEFAULT_VALUE;
    }

    const parsed = JSON.parse(stored) as {
      usr?: unknown;
      templateId?: unknown;
      loadDemoData?: unknown;
      loadOrgOptionsSampleData?: unknown;
      orgOptions?: unknown;
      useCase?: unknown;
      startView?: unknown;
      assistantPrompt?: unknown;
      genogramSourcePromptKey?: unknown;
      genogramStartMode?: unknown;
      networkStartPanel?: unknown;
      networkDialectId?: unknown;
      genoWizard?: unknown;
      hierarchyChartWizard?: unknown;
      startIntent?: unknown;
    } | null;
    const { isNewUser, isInvited } = parseFlags(parsed?.usr ?? null);
    const templateId = typeof parsed?.templateId === 'string' && parsed.templateId.length > 0
      ? parsed.templateId
      : null;

    const rawGenogramStartMode =
      typeof parsed?.genogramStartMode === 'string' ? parsed.genogramStartMode : null;
    const genogramStartMode: OnboardingContextValue['genogramStartMode'] =
      rawGenogramStartMode === 'blank-direct' || rawGenogramStartMode === 'scenario'
        ? rawGenogramStartMode
        : null;

    const useCase = typeof parsed?.useCase === 'string'
      ? parsed.useCase
      : genogramStartMode === 'blank-direct'
        ? 'blank'
        : null;
    const startView = typeof parsed?.startView === 'string'
      ? parsed.startView
      : genogramStartMode === 'blank-direct'
        ? 'blank'
        : null;

    const startIntent = coerceStartIntent(parsed?.startIntent);

    // Migration derivations — the deprecated per-domain fields fall back to
    // the typed intent when the legacy blob keys are absent, so consumers
    // keep working while they migrate to `startIntent`.
    const legacyNetworkPanel =
      parsed?.networkStartPanel === 'shapes' ||
      parsed?.networkStartPanel === 'import' ||
      parsed?.networkStartPanel === 'resources'
        ? parsed.networkStartPanel
        : null;
    const derivedNetworkPanel =
      startIntent?.panel === 'shapes' ||
      startIntent?.panel === 'import' ||
      startIntent?.panel === 'resources'
        ? startIntent.panel
        : null;
    return {
      isNewUser,
      isInvited,
      sourceTemplateId: templateId,
      loadDemoData: parsed?.loadDemoData === true,
      loadOrgOptionsSampleData: parsed?.loadOrgOptionsSampleData === true,
      orgOptions: parsed?.orgOptions === true,
      useCase,
      startView,
      assistantPrompt: typeof parsed?.assistantPrompt === 'string' ? parsed.assistantPrompt : null,
      genogramSourcePromptKey:
        typeof parsed?.genogramSourcePromptKey === 'string' ? parsed.genogramSourcePromptKey : null,
      genogramStartMode,
      networkStartPanel: legacyNetworkPanel ?? derivedNetworkPanel,
      networkDialectId:
        typeof parsed?.networkDialectId === 'string' && parsed.networkDialectId.length > 0
          ? parsed.networkDialectId
          : null,
      genoWizard: typeof parsed?.genoWizard === 'string' ? parsed.genoWizard : null,
      hierarchyChartWizard:
        typeof parsed?.hierarchyChartWizard === 'string' ? parsed.hierarchyChartWizard : null,
      startIntent,
    };
  } catch (error) {
    console.warn('[OnboardingContext] Failed to read onboarding context', error);
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // noop
    }
    return DEFAULT_VALUE;
  }
}

/** Onboarding intents are single-use: `readAndClear` reads the blob and
 *  immediately removes it so a refresh can't re-fire the intent. That
 *  read-and-clear is a side effect, which makes it unsafe to call from a
 *  `useState` initializer — React StrictMode (dev) double-invokes
 *  initializers and remounts the provider, so the first invocation
 *  consumes (and deletes) the blob and the second reads an empty store,
 *  silently resetting `loadDemoData` (and every other intent) to false.
 *  That's what dropped "Explore a demo" in dev. Memoize the consumed
 *  value per diagramId in-memory so every read in the same page session
 *  yields the same result; like the cleared sessionStorage, the cache is
 *  gone on a full reload, preserving the single-use-per-session contract. */
const consumedOnboarding = new Map<string, OnboardingContextValue>();

function consumeOnboarding(diagramId: string): OnboardingContextValue {
  const cached = consumedOnboarding.get(diagramId);
  if (cached) {
    return cached;
  }
  const value = readAndClear(diagramId);
  consumedOnboarding.set(diagramId, value);
  return value;
}

interface OnboardingProviderProps {
  diagramId: string;
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ diagramId, children }) => {
  const [state, setState] = useState<OnboardingContextValue>(() => consumeOnboarding(diagramId));
  const previousDiagramId = useRef(diagramId);

  useEffect(() => {
    if (previousDiagramId.current === diagramId) {
      return;
    }
    previousDiagramId.current = diagramId;
    setState(consumeOnboarding(diagramId));
  }, [diagramId]);

  return (
    <OnboardingContext.Provider value={state}>
      {children}
    </OnboardingContext.Provider>
  );
};

export function useOnboardingContext(): OnboardingContextValue {
  return useContext(OnboardingContext);
}
